/**
 * quickPlayStore — tiny module-level store shared by every hook instance in the
 * Solana Quick Play flow (useEnableSessionSigner, useSolanaQuickPlay,
 * useApproveGate, useSolanaMoney).
 *
 * Why it exists: the enable flow spans THREE independent hook instances — the
 * stake-screen gate (or Settings) calls delegateWallet, the App-scoped daemon
 * owns the wallet→Vault sweep, and the gate's money hook polls the Vault. They
 * only talked through Privy's `user` object, so if Privy lagged (or failed to)
 * refresh `linkedAccounts[].delegated` after the consent modal, the whole chain
 * stalled: the button never flipped, the daemon never deposited, and clicking
 * "approve" looked dead. This store carries the facts across instances:
 *
 *   - delegatedAddress: optimistic "delegation succeeded" flag, set the moment
 *     delegateWallet() resolves (Privy only resolves it after the user accepted
 *     AND the server registered the delegation — safe to trust).
 *   - sweeping / error: live state of the quick-play deposit so gates can show
 *     honest progress instead of a misleading "$0.00" flash.
 *   - sweeper registration: the daemon registers its sweep so enable flows can
 *     fund the Vault immediately instead of waiting for the next poll tick.
 *   - vault-dirty: fired after a deposit lands so money hooks re-read at once.
 */

type Listener = () => void;

export interface QuickPlayState {
  /** Wallet we KNOW is delegated this session (delegateWallet resolved). */
  delegatedAddress: string | null;
  /** A quick-play deposit (wallet → Vault) is in flight. */
  sweeping: boolean;
  /** Last sweep/delegation error, cleared on the next success. */
  error: string | null;
}

let state: QuickPlayState = { delegatedAddress: null, sweeping: false, error: null };
const listeners = new Set<Listener>();

function set(patch: Partial<QuickPlayState>): void {
  // Skip the emit when nothing changes — sweeps run on a 15s tick and most are
  // no-ops, so an unconditional emit would re-render every subscriber (gates,
  // session hooks) twice per tick for nothing.
  const changed = (Object.keys(patch) as (keyof QuickPlayState)[]).some(
    (k) => patch[k] !== state[k],
  );
  if (!changed) return;
  state = { ...state, ...patch };
  for (const l of [...listeners]) l();
}

/** useSyncExternalStore-compatible subscribe/getSnapshot pair. */
export function subscribeQuickPlay(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
export function quickPlayState(): QuickPlayState {
  return state;
}

export function markDelegated(address: string): void {
  set({ delegatedAddress: address, error: null });
}
export function clearDelegated(): void {
  set({ delegatedAddress: null });
}
export function setQuickPlaySweeping(sweeping: boolean): void {
  set({ sweeping });
}
export function setQuickPlayError(error: string | null): void {
  set({ error });
}

// ── Vault-dirty: "a deposit just landed, re-read balances now" ──────────────
const vaultListeners = new Set<Listener>();
export function subscribeVaultDirty(l: Listener): () => void {
  vaultListeners.add(l);
  return () => vaultListeners.delete(l);
}
export function bumpVaultDirty(): void {
  for (const l of [...vaultListeners]) l();
}

// ── Sweeper registration (single App-scoped daemon owns the sweep) ──────────
let sweeper: (() => Promise<void>) | null = null;
export function registerQuickPlaySweeper(fn: () => Promise<void>): () => void {
  sweeper = fn;
  return () => {
    if (sweeper === fn) sweeper = null;
  };
}
/** Run the daemon's sweep now (no-op when the daemon isn't mounted). */
export async function requestQuickPlaySweep(): Promise<void> {
  if (sweeper) await sweeper();
}
